import React, { useState, useMemo } from 'react';
import { Modal, Form, Input, Button, Tooltip, message, Select } from 'antd';
import commonS from 'styles/common.module.less';
import s from './s.module.less';
import { TMedicationSetting, type TObjectAction, type TServiceSetting } from 'types/helloKlarity';
import KlarityServiceSelect from 'components/form/KlarityServiceSelect';
import FileUpload from 'components/form/FileUpload';
import { klarityMedicationUpdate, klarityServiceUpdate } from 'api/operation/pageManagement';
import { REQUIRED_FIELD } from 'constants/common';
import { ISpecialty } from 'types/operation';

type Props = {
    currentIndex: number;
    item?: TMedicationSetting;
    list: TMedicationSetting[];
    specialties: ISpecialty[];
    currentSpecialty?: ISpecialty;
    onCancel?: () => void;
    onSuccess?: (obj: TObjectAction) => void;
    type?: 'edit' | 'add';
};

const EditMedication = ({
    currentIndex,
    item,
    list,
    specialties,
    currentSpecialty,
    onCancel,
    onSuccess,
    type = 'edit',
}: Props) => {
    const [submiting, setSubmiting] = useState(false);
    const [form] = Form.useForm<TMedicationSetting & { active: boolean }>();
    const isEdit = !!item?.id;
    const specialtyOptions = useMemo(() => {
        return specialties.map((e: ISpecialty) => ({
            label: e.specialtyName,
            value: e.id,
        }));
    }, [specialties]);

    const handleCancel = () => {
        form.resetFields();
        onCancel?.();
    };

    const onSave = async () => {
        setSubmiting(true);
        try {
            const formValues = await form.validateFields();
            const id = item?.id;

            const params: TMedicationSetting = {
                tag: formValues.tag,
                subtext: formValues.subtext,
                imageUrl: formValues.imageUrl,
                pageId: item?.pageId ?? 1,
                seq: item?.seq ?? 1,
                // 开头必须http、https或者/ ,否则就给他强制加https吧
                medicationPageUrl: (formValues?.medicationPageUrl!.startsWith('http') || formValues?.medicationPageUrl!.startsWith('https') || formValues?.medicationPageUrl!.startsWith('/')) ? formValues?.medicationPageUrl! : `https://${formValues?.medicationPageUrl!}`,
                title: formValues.title,
                specialtyId: formValues.specialtyId,
            };
            if (isEdit) {
                params.id = id;
            }
            const { data } = await klarityMedicationUpdate(params);

            if (data.code === 200) {
                message.success('Medication updated successfully');
                onSuccess?.({
                    type: 'MEDICATION_EDIT',
                });
                form.resetFields();
            }
        } catch (error) {
            console.error(error);
        } finally {
            setSubmiting(false);
        }
    };

    // const getMedicationUrl = (url: string) => {
    //     if (url.indexOf('https://') === 0 || url.indexOf('http://') === 0) {
    //         return url;
    //     }
    //     return `https://${url}`;
    // };

    React.useEffect(() => {
        if (type === 'edit' && item) {
            form.setFieldsValue({
                medicationPageUrl: item.medicationPageUrl,
                subtext: item.subtext,
                imageUrl: item.imageUrl,
                tag: item.tag,
                specialtyId: item.specialtyId,
                title: item.title,
            });
        } else {
            form.resetFields();
            if (currentSpecialty) {
                form.setFieldsValue({
                    specialtyId: currentSpecialty.id,
                });
            }
        }
    }, [form, item, type, currentSpecialty]);

    return (
        <Modal
            title={type === 'edit' ? 'Edit Medication' : 'Add Medication'}
            closable
            className={commonS.modalFixHeightWrap}
            width="500px"
            destroyOnClose
            onCancel={handleCancel}
            okText="Save"
            open
            footer={
                <div>
                    <Button onClick={handleCancel}>Cancel</Button>
                    <Button loading={submiting} type="primary" onClick={onSave}>
                        Save changes
                    </Button>
                </div>
            }
        >
            <div className={s.wrap}>
                <Form
                    form={form}
                    className={commonS.formStyle1}
                    autoComplete="off"
                    layout="vertical"
                    scrollToFirstError
                >
                    <>
                        <Form.Item
                            className={s.rowItem}
                            label="Page link"
                            name="medicationPageUrl"
                            rules={[
                                { required: true, message: REQUIRED_FIELD },
                            ]}
                        >
                            <Input
                                placeholder="http://"
                                // maxLength={100}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                        <Form.Item
                            className={s.rowItem}
                            label="Specialty"
                            name="specialtyId"
                            rules={[
                                { required: true, message: REQUIRED_FIELD },
                            ]}
                        >
                            <Select
                                options={specialtyOptions}
                            />
                        </Form.Item>
                        <Form.Item
                            className={s.rowItem}
                            label="Name"
                            name="title"
                            rules={[
                                { required: true, message: REQUIRED_FIELD },
                            ]}
                        >
                            <Input maxLength={30} showCount style={{ width: '100%' }} placeholder="Ex. Phentermine" />
                        </Form.Item>
                        <Form.Item
                            className={s.rowItem}
                            label="Subtext"
                            name="subtext"
                            rules={[
                                { required: true, message: REQUIRED_FIELD },
                            ]}
                        >
                            <Input maxLength={30} style={{ width: '100%' }} showCount />
                        </Form.Item>
                        {/* <Form.Item
                            className={s.rowItem}
                            label="Tag"
                            name="tag"
                            rules={[
                                { required: true, message: REQUIRED_FIELD },
                            ]}
                        >
                            <Input maxLength={30} style={{ width: '100%' }} />
                        </Form.Item> */}
                        <Form.Item
                            className={s.rowItem}
                            label={
                                <div className={s.formLabel}>
                                    Image
                                    <Tooltip
                                        overlayClassName={s.tipCard}
                                        overlayInnerStyle={{ padding: '12px' }}
                                        title={
                                            <div className={s.tipText}>
                                                Image should have a transparent background and be
                                                sized 278x128 pixels.
                                            </div>
                                        }
                                    >
                                        <div className={s.questionMarkIcon} />
                                    </Tooltip>
                                </div>
                            }
                            name={['imageUrl']}
                            rules={[{ required: true, message: 'Image is required' }]}
                        >
                            <FileUpload />
                        </Form.Item>
                    </>
                </Form>
            </div>
        </Modal>
    );
};

export default EditMedication;
